<template>
  <v-container class="px-8">
    <div class="py-3 pl-2 flex-grow-0 flex-shrink-0">
      <div class="d-flex flex-row justify-space-between align-end">
        <div class="text-h4 font-weight-medium">
          {{ $t("navbar.menu.contacts") }}
        </div>
        <v-btn color="primary" @click="newContactDialog = true">{{ $t("contacts.title") }}</v-btn>
      </div>
    </div>

    <v-card>
      <v-card-text>
        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th>{{ $t("common.name") }}</th>
                <th>{{ $t("common.phone") }}</th>
                <th>{{ $t("contacts.availability") }}</th>
                <th>{{ $t("contacts.details") }}</th>
                <th>{{ $t("contacts.actions") }}</th>
              </tr>
            </thead>
            <tbody v-if="Object.keys(contacts).length > 0">
              <tr v-for="contact in contacts" :key="contact.id">
                <td>{{ contact.name }}</td>
                <td style="min-width: 150px">{{ contact.phone }}</td>
                <td style="min-width: 160px">{{ getAvailability(contact) }}</td>
                <td style="max-width: 10%">{{ contact.details }}</td>

                <td>
                  <v-menu offset-y>
                    <template v-slot:activator="{ on }">
                      <v-btn text icon v-on="on">
                        <v-icon>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item @click="edit(contact)">
                        <v-list-item-title>{{ $t("common.edit") }}</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="remove(contact)">
                        <v-list-item-title>{{ $t("common.delete") }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td>{{ $t("contacts.noContacts") }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <v-dialog v-model="newContactDialog" persistent max-width="600px">
          <v-form ref="form" v-model="formValid">
            <v-card>
              <v-card-title>
                <span class="headline">{{ $t("contacts.title") }}</span>
              </v-card-title>
              <v-card-text>
                <v-container>
                  <v-row dense>
                    <v-col cols="7">
                      <v-text-field
                        name="name"
                        v-model="name"
                        :rules="nameRules"
                        :label="this.$t('common.name')"
                        required
                      ></v-text-field>

                      <v-text-field
                        name="phone"
                        v-model="phoneNumber"
                        @keyup="formatPhone"
                        :rules="phoneRules"
                        :label="this.$t('common.phone')"
                        :hint="this.$t('common.name')"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="5" class="my-auto pl-3">
                      <p>{{ $t("contacts.availability") }}</p>
                      <v-checkbox
                        color="primary"
                        :hint="this.$t('contacts.availabilityHint')"
                        persistent-hint
                        v-model="isAlwaysAvailable"
                        :label="this.$t('contacts.availabilityLabel')"
                      ></v-checkbox>
                    </v-col>
                    <v-col v-if="newContactDialog" v-show="!isAlwaysAvailable" cols="12">
                      <div class="d-flex">
                        <TimePickerField
                          :label="this.$t('contacts.availabilityStart')"
                          :rules="isAlwaysAvailable ? [] : [rules.required]"
                          v-model="startTime"
                        />
                        <TimePickerField
                          :label="this.$t('contacts.availabilityEnd')"
                          :rules="isAlwaysAvailable ? [] : [rules.required]"
                          v-model="endTime"
                        />
                      </div>
                    </v-col>
                    <v-col cols="12">
                      <v-textarea
                        name="input-7-1"
                        rows="3"
                        :label="this.$t('contacts.detailsLabel')"
                        v-model="details"
                        :hint="this.$t('contacts.detailsHint')"
                      ></v-textarea>
                    </v-col>
                  </v-row>
                  <p v-show="feedback" class="error--text">{{ feedback }}</p>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" text @click="closeDialog">{{ $t("common.close") }}</v-btn>
                <v-btn color="primary" :loading="buttonLoading" @click="saveContact()">
                  {{ $t("common.save") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-form>
        </v-dialog>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { parsePhoneNumberFromString, AsYouType } from "libphonenumber-js";
import database from "@/firebase/database";
import TimePickerField from "@/components/Base/TimePickerField";

export default {
  name: "Contacts",
  components: {
    TimePickerField,
  },
  data() {
    return {
      newContactDialog: false,
      isAlwaysAvailable: true,
      startTime: null,
      endTime: null,
      feedback: null,
      rules: {
        required: (value) => !!value || "Required.",
      },
      formValid: false,
      contactId: null,
      name: "",
      phoneNumber: "",
      details: "",
      buttonLoading: false,

      nameRules: [(v) => !!v || "Name is required"],
      phoneRules: [
        (v) => {
          const msg = "Invalid format, Dont forget your country code";
          if (!v) {
            return msg;
          }
          const phone = parsePhoneNumberFromString(v);
          if (phone) {
            return phone.isValid() || msg;
          }
          return v.length < 6 || msg;
        },
      ],
    };
  },
  methods: {
    getAvailability(contact) {
      if (contact.always_available || contact.always_available === undefined) {
        return this.$t("common.always");
      }
      const startTime = this.formatDateStr(contact.start_time);
      const endTime = this.formatDateStr(contact.end_time);
      return `${startTime} ${this.$t("common.time.to")} ${endTime}`;
    },
    formatDateStr(minutes, format) {
      if (minutes === undefined) {
        return undefined;
      }
      const date = new Date(minutes * 60000);
      return this.$options.filters.zonedDate(date, format || "p", "GMT", true);
    },
    formatPhone() {
      this.phoneNumber = new AsYouType().input(this.phoneNumber);
    },
    edit(contact) {
      console.log("edit contact");
      console.log(contact);
      this.name = contact.name;
      this.phoneNumber = contact.phone;
      this.details = contact.details || "";
      this.isAlwaysAvailable =
        contact.always_available === undefined ? true : contact.always_available;
      this.startTime = this.formatDateStr(contact.start_time, "HH:mm");
      this.endTime = this.formatDateStr(contact.end_time, "HH:mm");
      this.newContactDialog = true;
      this.contactId = contact.id;
    },
    closeDialog() {
      this.$refs.form.reset();
      this.contactId = null;
      this.newContactDialog = false;
    },
    remove(contact) {
      console.log("removing contact");
      console.log(contact);
      const tripId = this.$store.getters.currentTripId;
      database.deleteContact(tripId, contact.id);
    },
    saveContact() {
      if (!this.formValid) {
        this.feedback = this.$t("common.fillsRequired");
        return;
      }
      this.feedback = null;

      const tripId = this.$store.getters.currentTripId;

      const contact = {
        name: this.name,
        phone: this.phoneNumber,
        always_available: this.isAlwaysAvailable,
        details: this.details,
      };

      if (!this.isAlwaysAvailable) {
        const startTime = new Date(`1970-01-01T${this.startTime}`);
        const endTime = new Date(`1970-01-01T${this.endTime}`);
        console.log(startTime);
        console.log(this.startTime);
        contact.start_time = startTime.getMinutes() + startTime.getHours() * 60;
        contact.end_time = endTime.getMinutes() + endTime.getHours() * 60;
      }

      console.log(contact);

      if (this.contactId) {
        database.updateContect(tripId, this.contactId, contact);
      } else {
        database.pushNewContact(tripId, contact);
      }

      this.closeDialog();
    },
  },
  computed: {
    contacts() {
      return this.$store.state.trip.contacts;
    },
  },
};
</script>
